import React, { useState } from "react";
import { Check, ChevronDown } from "lucide-react";
import { PRO_FEATURES } from "../data/subscriptionPlans";

interface PlanIncludesProps {
  /** What the panel is called, so two cards on one screen cannot both claim
   *  the same id. Only ever one is drawn today; the prop is what keeps that
   *  from being a thing to remember. */
  id?: string;
}

/**
 * "What's included", folded away at the foot of whichever card names the plan.
 *
 * The list is the same five lines on every workspace and on every way of paying
 * for it — a subscription, a trial — which is why it is closed to begin with and
 * why it lives here rather than in either card: there is one product, and what
 * it includes must not drift between the card that says you are paying for it
 * and the card that says you are trying it.
 */
const PlanIncludes: React.FC<PlanIncludesProps> = ({ id = "plan-features" }) => {
  const [showFeatures, setShowFeatures] = useState(false);

  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-3 border-t-[0.5px] border-[#EFFBFD] px-5 py-3 3xl:px-6">
        <button
          type="button"
          onClick={() => setShowFeatures((open) => !open)}
          aria-expanded={showFeatures}
          aria-controls={id}
          className="flex items-center gap-1.5 text-[14px] 3xl:text-[15px] text-[#007E8C] hover:text-brand-dark transition-colors"
        >
          What&apos;s included
          {/* Turned rather than swapped for an up arrow: the same mark moving
              says the same panel is opening and closing. */}
          <ChevronDown
            aria-hidden="true"
            className={`h-4 w-4 transition-transform ${
              showFeatures ? "rotate-180" : ""
            }`}
          />
        </button>
      </div>

      {showFeatures && (
        <ul
          id={id}
          className="flex flex-col gap-3 border-t border-line px-5 py-4 3xl:px-6 3xl:gap-4"
        >
          {PRO_FEATURES.map((feature) => (
            <li key={feature.name} className="flex gap-2.5">
              <Check
                aria-hidden="true"
                strokeWidth={2.5}
                className="mt-[3px] h-4 w-4 flex-shrink-0 text-brand"
              />
              <p className="text-[13px] 3xl:text-[14px] leading-[1.55] text-ink-muted">
                <span className="font-medium text-ink">{feature.name}</span>
                {" - "}
                {feature.description}
              </p>
            </li>
          ))}
        </ul>
      )}
    </>
  );
};

export default PlanIncludes;
